import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as api from '../services/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const toggleLang = () => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/5 rounded-full" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl" />
      </div>

      <div className="absolute top-4 right-4 z-20">
        <button onClick={toggleLang} className="text-sm text-blue-200 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition">
          {i18n.language === 'pl' ? 'English' : 'Polski'}
        </button>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-4 text-center border-b border-gray-100">
            <Link to="/">
              <img src="/logo_full.png" alt="SWIBI" className="h-10 mx-auto" />
            </Link>
          </div>

          <div className="p-8">
            <h1 className="text-lg font-bold text-gray-800 text-center mb-1">{t('auth.forgotTitle')}</h1>
            <p className="text-gray-400 text-sm text-center mb-6">{t('auth.forgotSubtitle')}</p>

            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {sent ? (
              <div className="text-center space-y-4">
                <div className="flex flex-col items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  <CheckCircle size={28} className="text-green-500" />
                  {t('auth.resetEmailSent')}
                </div>
                <Link to="/login" className="inline-block text-blue-600 hover:text-blue-700 text-sm font-medium">
                  {t('auth.mfaBackToLogin')}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.emailLabel')}</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('auth.emailPlaceholder')} required autoFocus />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50 transition shadow-lg shadow-blue-600/25">
                  {loading ? t('auth.loggingIn') : t('auth.forgotSubmit')}
                </button>
                <Link to="/login" className="block text-center text-sm text-gray-500 hover:text-gray-700">
                  {t('auth.mfaBackToLogin')}
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
